import { useEffect, useRef, useState } from "react";
import Lenis from "lenis";
import { AnimatePresence } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";
import Seo from "@/components/Seo";
import { SealScreen } from "@/components/SealScreen";
import { Hero } from "@/components/Hero";
import { Marquee } from "@/components/Marquee";
import { Itinerary } from "@/components/Itinerary";
import { ClosingFooter } from "@/components/ClosingFooter";
import { weddingConfig } from "@/config/weddingConfig";

export default function App() {
  const [opened, setOpened] = useState(false);
  const [muted, setMuted] = useState(false);
  const [musicReady, setMusicReady] = useState(true);
  const lenisRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    lenisRef.current = lenis;
    lenis.stop();
    let rafId;
    const loop = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = opened ? "" : "hidden";
    if (opened) lenisRef.current?.start();
  }, [opened]);

  // Called synchronously from the tap gesture so browsers allow playback.
  const startMusic = () => {
    audioRef.current?.play().catch(() => {});
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.muted = !audio.muted;
    setMuted(audio.muted);
  };

  const { meta, music } = weddingConfig;

  return (
    <div className="min-h-screen bg-emerald-deep font-sans text-cream antialiased">
      <Seo
        title={meta.title}
        siteName={meta.siteName}
        description={meta.description}
        image="/og-cover.png"
      />

      <audio
        ref={audioRef}
        src={music.src}
        loop
        preload="auto"
        onError={() => setMusicReady(false)}
      />

      <AnimatePresence>
        {!opened && (
          <SealScreen
            key="seal"
            onOpenStart={startMusic}
            onOpen={() => setOpened(true)}
          />
        )}
      </AnimatePresence>

      {opened && musicReady && (
        <button
          type="button"
          data-testid="music-toggle"
          onClick={toggleMute}
          aria-label={muted ? "Unmute music" : "Mute music"}
          className="fixed bottom-5 right-5 z-40 flex h-11 w-11 cursor-pointer items-center justify-center rounded-full border border-gold/50 bg-emerald-deep/80 text-gold shadow-lg backdrop-blur-sm transition-colors duration-300 hover:bg-emerald-light focus:outline-none focus-visible:ring-2 focus-visible:ring-gold"
        >
          {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
        </button>
      )}

      <main data-testid="invitation-main">
        <Hero opened={opened} />
        <Marquee />
        <Itinerary />
        <ClosingFooter />
      </main>
    </div>
  );
}
